import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ShieldAlert,
  Activity,
  Download,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

interface ParticipationMonitoringProps {
  campaignId: number;
  campaignStatus: string;
  // Metadados da campanha para enriquecer o Excel
  campaignMeta?: {
    channelName?: string | null;
    clientName?: string | null;
    clientCnpj?: string | null;
    templateId?: number | null;
    templateVersion?: string | null;
    kMin?: number;
  };
}

// Cores para os status
const STATUS_COLORS = {
  SEM_RESPOSTA: "#ef4444",   // red-500
  ABAIXO_KMIN: "#f59e0b",   // amber-500
};

type ExportFilter = "ALL" | "SEM_RESPOSTA" | "ABAIXO_KMIN";

interface MonitoringItem {
  sector: string;
  jobTitle: string;
  totalRespostas: number;
  employeesCount: number;
  kMin: number;
  status: "SEM_RESPOSTA" | "ABAIXO_KMIN" | "OK";
}

interface MonitoringData {
  campaignId: number;
  kMin: number;
  status: string;
  semRespostaCount: number;
  abaixoKMinCount: number;
  totalCombinations: number;
  okCount: number;
  items: MonitoringItem[];
}

/**
 * Gera e baixa o arquivo XLSX de monitoramento de participação.
 * Segue exatamente o template "Pendencias_Campanha_SOLUTIS.xlsx".
 * Estratégia: client-side (dataset pequeno, dados já carregados).
 */
function exportToExcel(
  data: MonitoringData,
  campaignId: number,
  filter: ExportFilter,
  meta?: ParticipationMonitoringProps["campaignMeta"]
) {
  try {
    const now = new Date();
    const timeStr = now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    const exportedAt = `${now.toLocaleDateString("pt-BR")} ${timeStr}`;

    const channelName = meta?.channelName || "—";
    const clientName = meta?.clientName || "—";
    const clientCnpj = meta?.clientCnpj || "";

    // Filtrar itens conforme seleção
    let filteredItems: MonitoringItem[];
    let pendenciasCount: number;
    let filterLabel: string;

    if (filter === "SEM_RESPOSTA") {
      filteredItems = data.items.filter((i) => i.status === "SEM_RESPOSTA");
      pendenciasCount = data.semRespostaCount;
      filterLabel = "Sem Resposta";
    } else if (filter === "ABAIXO_KMIN") {
      filteredItems = data.items.filter((i) => i.status === "ABAIXO_KMIN");
      pendenciasCount = data.abaixoKMinCount;
      filterLabel = "Abaixo do K mín.";
    } else {
      // ALL = SEM_RESPOSTA + ABAIXO_KMIN
      filteredItems = data.items;
      pendenciasCount = data.semRespostaCount + data.abaixoKMinCount;
      filterLabel = "Sem Resposta";
    }

    const wb = XLSX.utils.book_new();

    // ─────────────────────────────────────────────────────────────────────────
    // ABA 1: "Pendências - Sem Resposta"  (nome fixo conforme template)
    // ─────────────────────────────────────────────────────────────────────────
    const statusLabel = (s: string) => {
      if (s === "SEM_RESPOSTA") return "Sem Resposta";
      if (s === "ABAIXO_KMIN") return "Abaixo do K mín.";
      return "OK";
    };

    // Linhas de metadados (linhas 1-4 do template)
    const cnpjPart = clientCnpj ? ` (CNPJ: ${clientCnpj})` : "";
    const pendenciasRows: (string | number | null)[][] = [
      [`Pendências de Participação — Campanha #${campaignId}`, null, null, null, null, null],
      [`Cliente: ${clientName}${cnpjPart}`, null, null, null, null, null],
      [`Canal: ${channelName} | Gerado em: ${exportedAt}`, null, null, null, null, null],
      [`Combinações Setor + Cargo sem resposta: ${pendenciasCount}`, null, null, null, null, null],
      // Linha 5 em branco
      [null, null, null, null, null, null],
      // Linha 6: cabeçalho da tabela
      ["#", "Setor", "Cargo", "Headcount Importado", "Respostas Recebidas", "Status"],
    ];

    // Linhas de dados (a partir da linha 7)
    filteredItems.forEach((item, idx) => {
      pendenciasRows.push([
        idx + 1,
        item.sector,
        item.jobTitle,
        item.employeesCount, // Headcount Importado (employeesCount da campaign_scope_items)
        item.totalRespostas,
        statusLabel(item.status),
      ]);
    });

    const wsPendencias = XLSX.utils.aoa_to_sheet(pendenciasRows);
    // Larguras exatas do template
    wsPendencias["!cols"] = [
      { wch: 6 },   // # (col A)
      { wch: 35 },  // Setor (col B)
      { wch: 40 },  // Cargo (col C)
      { wch: 20 },  // Headcount Importado (col D)
      { wch: 22 },  // Respostas Recebidas (col E)
      { wch: 16 },  // Status (col F)
    ];
    XLSX.utils.book_append_sheet(wb, wsPendencias, "Pendências - Sem Resposta");

    // ─────────────────────────────────────────────────────────────────────────
    // ABA 2: "Resumo por Setor"
    // ─────────────────────────────────────────────────────────────────────────
    // Calcular resumo por setor a partir dos items completos (data.items)
    // Usamos data.items que contém TODOS os itens (sem filtro)
    const sectorMap = new Map<string, { total: number; comResposta: number; semResposta: number }>();
    data.items.forEach((item) => {
      const entry = sectorMap.get(item.sector) || { total: 0, comResposta: 0, semResposta: 0 };
      entry.total += 1;
      if (item.totalRespostas >= 1) {
        entry.comResposta += 1;
      } else {
        entry.semResposta += 1;
      }
      sectorMap.set(item.sector, entry);
    });

    // Ordenar por % pendente decrescente (maior pendência primeiro)
    const sectorEntries = Array.from(sectorMap.entries()).sort((a, b) => {
      const pctA = a[1].total > 0 ? a[1].semResposta / a[1].total : 0;
      const pctB = b[1].total > 0 ? b[1].semResposta / b[1].total : 0;
      return pctB - pctA;
    });

    const resumoRows: (string | number | null)[][] = [
      [`Resumo de Pendências por Setor — Campanha #${campaignId}`, null, null, null, null],
      [`Cliente: ${clientName} | Gerado em: ${exportedAt}`, null, null, null, null],
      // Linha 3 em branco
      [null, null, null, null, null],
      // Linha 4: cabeçalho
      ["Setor", "Total de Cargos", "Com Resposta", "Sem Resposta", "% Pendente"],
    ];

    sectorEntries.forEach(([sector, stats]) => {
      const pct = stats.total > 0 ? Math.round((stats.semResposta / stats.total) * 100) : 0;
      resumoRows.push([
        sector,
        stats.total,
        stats.comResposta,
        stats.semResposta,
        `${pct}%`,
      ]);
    });

    const wsResumo = XLSX.utils.aoa_to_sheet(resumoRows);
    wsResumo["!cols"] = [
      { wch: 35 },  // Setor
      { wch: 18 },  // Total de Cargos
      { wch: 16 },  // Com Resposta
      { wch: 16 },  // Sem Resposta
      { wch: 14 },  // % Pendente
    ];
    XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo por Setor");

    // ── Nome do arquivo — padrão do template antigo ──────────────────────────
    // Formato: Pendencias_Campanha_<id>_<CLIENTE>.xlsx
    const safeClient = (clientName || "cliente")
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")  // remover acentos
      .replace(/[^a-zA-Z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "_")
      .toUpperCase()
      .slice(0, 30);
    const fileName = `Pendencias_Campanha_${campaignId}_${safeClient}.xlsx`;

    XLSX.writeFile(wb, fileName);
    return true;
  } catch (err) {
    console.error("[V90] Erro ao gerar Excel:", err);
    return false;
  }
}

export function ParticipationMonitoring({ campaignId, campaignStatus, campaignMeta }: ParticipationMonitoringProps) {
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading, error } = trpc.questionnaire.monitoring.getParticipationMonitoring.useQuery(
    { campaignId },
    {
      enabled: campaignStatus !== "DRAFT",
      refetchInterval: campaignStatus === "ACTIVE" ? 15000 : false, // Atualizar a cada 15s se ativa
    }
  );

  // Não exibir para DRAFT
  if (campaignStatus === "DRAFT") return null;

  // Loading state
  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Monitoramento de Participação da Campanha
          </CardTitle>
          <CardDescription>
            Carregando dados de participação...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
            </div>
            <Skeleton className="h-48" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Monitoramento de Participação da Campanha
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Não foi possível carregar os dados de monitoramento. Tente recarregar a página.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { semRespostaCount, abaixoKMinCount, totalCombinations, okCount, kMin, items } = data;
  const totalPendencias = semRespostaCount + abaixoKMinCount;

  // Handler de exportação
  const handleExport = async (filter: ExportFilter) => {
    setIsExporting(true);
    try {
      const success = exportToExcel(data, campaignId, filter, campaignMeta);
      if (success) {
        toast.success("Arquivo exportado com sucesso.");
      } else {
        toast.error("Não foi possível exportar. Tente novamente.");
      }
    } catch (err) {
      console.error("[V86] Erro inesperado na exportação:", err);
      toast.error("Não foi possível exportar. Tente novamente.");
    } finally {
      setIsExporting(false);
    }
  };

  // Botão de exportação (reutilizado em múltiplos estados)
  const ExportButton = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isExporting}
          className="gap-1.5"
        >
          {isExporting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Exportando...
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              Exportar Excel
              <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onClick={() => handleExport("ALL")}>
          <Download className="h-4 w-4 mr-2 text-muted-foreground" />
          Todas as pendências
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleExport("SEM_RESPOSTA")} disabled={semRespostaCount === 0}>
          <XCircle className="h-4 w-4 mr-2 text-red-500" />
          Somente Sem Resposta
          {semRespostaCount > 0 && (
            <span className="ml-auto text-xs text-muted-foreground">{semRespostaCount}</span>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport("ABAIXO_KMIN")} disabled={abaixoKMinCount === 0}>
          <ShieldAlert className="h-4 w-4 mr-2 text-amber-500" />
          Somente Abaixo do K mín.
          {abaixoKMinCount > 0 && (
            <span className="ml-auto text-xs text-muted-foreground">{abaixoKMinCount}</span>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Empty state: sem pendências
  if (totalPendencias === 0) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Monitoramento de Participação da Campanha
              </CardTitle>
              <CardDescription className="mt-1">
                Combinações Setor + Cargo que não receberam respostas ou não atingiram o K mínimo.
              </CardDescription>
            </div>
            <ExportButton />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
            <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400 shrink-0" />
            <div>
              <p className="font-medium text-emerald-800 dark:text-emerald-300">
                Todos os setores e cargos atingiram o K mínimo!
              </p>
              <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">
                {totalCombinations} combinações Setor+Cargo com pelo menos {kMin} respostas cada.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Dados para gráfico de pizza
  const pieData = [
    ...(semRespostaCount > 0 ? [{ name: "Sem Resposta", value: semRespostaCount, color: STATUS_COLORS.SEM_RESPOSTA }] : []),
    ...(abaixoKMinCount > 0 ? [{ name: `Abaixo de ${kMin}`, value: abaixoKMinCount, color: STATUS_COLORS.ABAIXO_KMIN }] : []),
  ];

  // Dados para barras textuais
  const maxCount = Math.max(semRespostaCount, abaixoKMinCount, 1);

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Monitoramento de Participação da Campanha
            </CardTitle>
            <CardDescription className="mt-1">
              Combinações Setor + Cargo que não receberam respostas ou não atingiram o K mínimo ({kMin}).
            </CardDescription>
          </div>
          <ExportButton />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Linha de cards de resumo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-100 dark:bg-red-950/50">
                    <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Sem Resposta</p>
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">{semRespostaCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-950/50">
                    <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Abaixo do K mín. ({kMin})</p>
                    <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{abaixoKMinCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-950/50">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">OK (≥ {kMin})</p>
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{okCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Gráficos: barras textuais + pizza */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Barras textuais */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Pendências por Status</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <XCircle className="h-3.5 w-3.5 text-red-500" />
                      Sem Resposta
                    </span>
                    <span className="text-sm font-bold text-red-600 dark:text-red-400">{semRespostaCount}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div
                      className="bg-red-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${Math.max((semRespostaCount / maxCount) * 100, semRespostaCount > 0 ? 4 : 0)}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />
                      Abaixo do K mín.
                    </span>
                    <span className="text-sm font-bold text-amber-600 dark:text-amber-400">{abaixoKMinCount}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div
                      className="bg-amber-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${Math.max((abaixoKMinCount / maxCount) * 100, abaixoKMinCount > 0 ? 4 : 0)}%` }}
                    />
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Total: {totalPendencias} pendência{totalPendencias !== 1 ? "s" : ""} de {totalCombinations} combinações
              </p>
            </div>

            {/* Gráfico de pizza */}
            {pieData.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Distribuição de Pendências</h4>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                      labelLine={false}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string) => [`${value} combinações`, name]}
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Tabela de pendências */}
          {items.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3">
                Detalhamento de Pendências ({items.length} {items.length === 1 ? "combinação" : "combinações"})
              </h4>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="text-left py-2.5 px-3 font-medium">Setor</th>
                      <th className="text-left py-2.5 px-3 font-medium">Cargo</th>
                      <th className="text-center py-2.5 px-3 font-medium">Respostas</th>
                      <th className="text-center py-2.5 px-3 font-medium">K mínimo</th>
                      <th className="text-center py-2.5 px-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={`${item.sector}-${item.jobTitle}-${idx}`} className="border-t hover:bg-muted/30">
                        <td className="py-2.5 px-3">{item.sector}</td>
                        <td className="py-2.5 px-3">{item.jobTitle}</td>
                        <td className="py-2.5 px-3 text-center font-mono">
                          <span className={item.totalRespostas === 0 ? "text-red-600 dark:text-red-400 font-bold" : "text-amber-600 dark:text-amber-400"}>
                            {item.totalRespostas}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center font-mono text-muted-foreground">{item.kMin}</td>
                        <td className="py-2.5 px-3 text-center">
                          {item.status === "SEM_RESPOSTA" ? (
                            <Badge variant="destructive" className="gap-1 text-xs">
                              <XCircle className="h-3 w-3" />
                              Sem Resposta
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="gap-1 text-xs border-amber-500 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30">
                              <ShieldAlert className="h-3 w-3" />
                              Abaixo do K mín.
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
